import React, { useCallback, useEffect, useState } from "react";

import { IOptionCode, IProduct } from "../../../models/catalogue.interfaces";
import { IProductID } from "../../../models/nominals";
import { intersection } from "../../../utils/sets";
import { useAppSelector } from "../../reducers";
import { tabFeatureAttributeBlock } from "../elementIDs";
import { ITabFeatureAttributeBlock_AttributeTab } from "../models.interfaces";
import { rootProductsSelector } from "../selectors";
import { TabFeatureAttributeBlockModelTabButton } from "./TabFeatureAttributeBlockModelTabButton";
import { TabFeatureAttributeBlockModelTabContent } from "./TabFeatureAttributeBlockModelTabContent";
import { TabFeatureAttributeBlockModelTabImage } from "./TabFeatureAttributeBlockModelTabImage";

import styles from "./TabFeatureAttributeBlockAttributeTabContent.module.scss";

type TabFeatureAttributeBlockAttributeTabContentProps = {
    attribute: IOptionCode;
    idx: number;
    tab: ITabFeatureAttributeBlock_AttributeTab;
    isSelected: boolean;
};

export const TabFeatureAttributeBlockAttributeTabContent: React.FC<
    TabFeatureAttributeBlockAttributeTabContentProps
> = ({ attribute, idx, tab, isSelected }) => {
    const rootProducts = useAppSelector((state) =>
        rootProductsSelector(state.configurator),
    );
    const [selectedModel, setSelectedModel] = useState<IProductID | null>(null);

    // Helper to get selected root product from redux
    const getSelectedRootProductFromRedux = useCallback(
        (rootProducts: IProduct[]): IProductID | null => {
            if (rootProducts.length <= 0) {
                return null;
            }
            const tabPIDs = new Set(tab.tabs.map((t) => t.model));
            const selectedPIDs = new Set(rootProducts.map((p) => p.id));
            const selectedPIDsWithTabs = intersection(tabPIDs, selectedPIDs);
            if (selectedPIDsWithTabs.size <= 0) {
                return null;
            }
            return [...selectedPIDsWithTabs][0];
        },
        [tab.tabs],
    );

    useEffect(() => {
        const reduxSelectedModel =
            getSelectedRootProductFromRedux(rootProducts);
        const next = reduxSelectedModel ?? tab.tabs[0]?.model ?? null;
        setSelectedModel((prev) => (prev === next ? prev : next));
    }, [rootProducts, tab.tabs, getSelectedRootProductFromRedux]);

    const handleClick = useCallback((model: IProductID) => {
        setSelectedModel((prev) => (prev === model ? prev : model));
    }, []);

    // Compute active model tab once
    const activeModelTab = selectedModel
        ? tab.tabs.find((t) => t.model === selectedModel) || tab.tabs[0]
        : tab.tabs[0];

    return (
        <article
            className={styles.root}
            id={tabFeatureAttributeBlock.attributeTabContent(tab, idx)}
            role="tabpanel"
            tabIndex={0}
            aria-labelledby={tabFeatureAttributeBlock.attributeTabButton(
                tab,
                idx,
            )}
            hidden={!isSelected}
        >
            <div className={styles.modelGraphic}>
                {activeModelTab && (
                    <TabFeatureAttributeBlockModelTabImage
                        key={String(activeModelTab.model)}
                        attrIdx={idx}
                        attrTab={tab}
                        modelIdx={tab.tabs.indexOf(activeModelTab)}
                        modelTab={activeModelTab}
                        isSelected={true}
                    />
                )}
            </div>
            <div className={styles.modelButtonsAndContent}>
                <div className={styles.modelButtons}>
                    {tab.tabs.map((modelTab, i: number) => (
                        <TabFeatureAttributeBlockModelTabButton
                            key={String(modelTab.model)}
                            attrIdx={idx}
                            attrTab={tab}
                            modelIdx={i}
                            modelTab={modelTab}
                            onClick={() => handleClick(modelTab.model)}
                            isSelected={
                                modelTab.model === activeModelTab?.model
                            }
                        />
                    ))}
                </div>
                <div className={styles.modelContent}>
                    {activeModelTab && (
                        <TabFeatureAttributeBlockModelTabContent
                            key={`${activeModelTab.model}`}
                            attribute={attribute}
                            attrIdx={idx}
                            attrTab={tab}
                            modelIdx={tab.tabs.indexOf(activeModelTab)}
                            modelTab={activeModelTab}
                            isSelected={true}
                        />
                    )}
                </div>
            </div>
        </article>
    );
};
